"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  SECONDARY_LOCALE,
  isSecondaryLocaleEnabled,
  type AppLocale,
} from "./config";
import { createTranslator, type TranslateFn } from "./translate";
import type { EnMessages } from "./types";

export type { EnMessages };

interface LocaleContextValue {
  locale: AppLocale;
  messages: EnMessages | null;
  switchLocale: (locale: AppLocale) => void;
  t: TranslateFn;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

let activeLocale: AppLocale = DEFAULT_LOCALE;

export const getActiveLocale = (): AppLocale => activeLocale;

export const formatNumber = (n: number): string =>
  n.toLocaleString(activeLocale === SECONDARY_LOCALE ? "en-US" : "fa-IR");

const FONT_STACKS: Record<AppLocale, string> = {
  fa: "var(--font-vazirmatn), system-ui, sans-serif",
  en: "var(--font-inter), Inter, system-ui, sans-serif",
};

export const applyDocumentLocale = (locale: AppLocale): void => {
  const root = document.documentElement;
  const { scrollX, scrollY } = window;
  root.setAttribute("lang", locale);
  root.setAttribute("dir", locale === DEFAULT_LOCALE ? "rtl" : "ltr");
  root.style.setProperty("--font-sans", FONT_STACKS[locale]);
  window.scrollTo(scrollX, scrollY);
  requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
};

const readStoredLocale = (): AppLocale => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`));
  return match?.[1] === SECONDARY_LOCALE ? SECONDARY_LOCALE : DEFAULT_LOCALE;
};

const persistLocale = (locale: AppLocale): void => {
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
};

const loadSecondaryMessages = (apply: (messages: EnMessages) => void): void => {
  import("./messages/en.json").then((module) => {
    apply(module.default);
  });
};

export function LocaleProvider({ children }: PropsWithChildren) {
  const [locale, setLocale] = useState<AppLocale>(DEFAULT_LOCALE);
  const [messages, setMessages] = useState<EnMessages | null>(null);

  activeLocale = locale;

  useEffect(() => {
    if (!isSecondaryLocaleEnabled() || readStoredLocale() !== SECONDARY_LOCALE) return;
    loadSecondaryMessages((loaded) => {
      setMessages(loaded);
      setLocale(SECONDARY_LOCALE);
      applyDocumentLocale(SECONDARY_LOCALE);
    });
  }, []);

  const switchLocale = useCallback((next: AppLocale) => {
    if (!isSecondaryLocaleEnabled()) return;
    persistLocale(next);
    if (next === DEFAULT_LOCALE) {
      setMessages(null);
      setLocale(DEFAULT_LOCALE);
      applyDocumentLocale(DEFAULT_LOCALE);
      return;
    }
    loadSecondaryMessages((loaded) => {
      setMessages(loaded);
      setLocale(SECONDARY_LOCALE);
      applyDocumentLocale(SECONDARY_LOCALE);
    });
  }, []);

  const t = useMemo(() => createTranslator(locale, messages), [locale, messages]);

  const value = useMemo(
    () => ({ locale, messages, switchLocale, t }),
    [locale, messages, switchLocale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

const useLocaleContext = (): LocaleContextValue => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("Locale hooks must be used inside LocaleProvider");
  return context;
};

export const useAppLocale = (): AppLocale => useLocaleContext().locale;
export const useMessages = (): EnMessages | null => useLocaleContext().messages;
export const useSwitchLocale = (): ((locale: AppLocale) => void) => useLocaleContext().switchLocale;
export const useT = (): TranslateFn => useLocaleContext().t;
